import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const IDLE_LIMIT_MS = 2 * 60 * 60 * 1000; // 2 hours
export const IDLE_WARN_MS = 2 * 60 * 1000; // countdown shown for the final 2 minutes
const LAST_ACTIVE_KEY = "bm_last_active";
export const IDLE_FLAG_KEY = "bm_idle_signout";

// localStorage can throw in private/locked-down browsers — never let that break login.
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
  del: (k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};
const touch = () => store.set(LAST_ACTIVE_KEY, String(Date.now()));
const isIdleExpired = () => {
  const last = Number(store.get(LAST_ACTIVE_KEY) || 0);
  return last > 0 && Date.now() - last > IDLE_LIMIT_MS;
};
const clearSession = (idle) => {
  store.del("bm_token");
  store.del(LAST_ACTIVE_KEY);
  if (idle) store.set(IDLE_FLAG_KEY, "1");
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = logged in
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = store.get("bm_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    if (isIdleExpired()) {
      clearSession(true);
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        store.del("bm_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  // Auto sign-out after IDLE_LIMIT_MS without any interaction, with a
  // countdown warning for the last IDLE_WARN_MS. While the warning is up,
  // passive activity (mouse/scroll) no longer counts — only "Stay signed in".
  const [idleRemaining, setIdleRemaining] = useState(null); // ms, null = no warning
  const warningRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    touch();
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "visibilitychange"];
    const onActivity = () => {
      if (warningRef.current || document.visibilityState === "hidden") return;
      touch();
    };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const timer = setInterval(() => {
      const last = Number(store.get(LAST_ACTIVE_KEY) || Date.now());
      const remaining = IDLE_LIMIT_MS - (Date.now() - last);
      if (remaining <= 0) {
        clearSession(true);
        window.location.href = "/login?reason=idle";
        return;
      }
      const warn = remaining <= IDLE_WARN_MS;
      warningRef.current = warn;
      setIdleRemaining(warn ? remaining : null);
    }, 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(timer);
    };
  }, [user]);

  function staySignedIn() {
    touch();
    warningRef.current = false;
    setIdleRemaining(null);
  }

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    store.set("bm_token", data.token);
    touch();
    setUser(data.user);
    return data.user;
  }

  function logout() {
    clearSession(false);
    setUser(false);
    window.location.href = "/login";
  }

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, idleRemaining, staySignedIn }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/** Staff open on their home store(s); owners open on all stores. */
export const homeStores = (user) => (user?.role === "staff" ? [...(user.outlet_ids || [])] : []);
